import { type ElementType, type ReactNode, useMemo } from 'react';

import { Button, type ButtonProps } from '@proton/atoms/Button/Button';
import { Tooltip } from '@proton/atoms/Tooltip/Tooltip';
import useConfig from '@proton/components/hooks/useConfig';
import type { PaymentFacade } from '@proton/components/payments/client-extensions';
import type { ProductParam } from '@proton/shared/lib/apps/product';
import clsx from '@proton/utils/clsx';
import isFunction from '@proton/utils/isFunction';

import { PAYMENT_METHOD_TYPES } from '../../core/constants';
import type { PlainPaymentMethodType } from '../../core/interface';
import type { PaymentTelemetryContext } from '../../telemetry/helpers';
import { checkoutTelemetry } from '../../telemetry/telemetry';
import type { TaxCountryHook } from '../billing-address/hooks/useTaxCountry';
import type { VatNumberHook } from '../billing-address/hooks/useVatNumber';
import { ApplePayButton } from './ApplePayButton';
import { ChargebeePaypalButton } from './ChargebeePaypalButton';
import { GooglePayButton } from './GooglePayButton';

export type PayButtonOnClickPayload = {
    type: 'paypal' | 'apple-pay' | 'google-pay' | 'card';
    source: 'fake-button' | 'real-button';
};

type Props = {
    taxCountry: TaxCountryHook;
    vatNumber?: VatNumberHook;
    disabled?: boolean;
    children: ReactNode;
    paymentFacade: PaymentFacade;
    suffix?: ReactNode | ((type: PlainPaymentMethodType | undefined) => ReactNode);
    as?: ElementType;
    paypalClassName?: string;
    formInvalid?: boolean;
    onClick?: (payload: PayButtonOnClickPayload) => void;
    product: ProductParam;
    telemetryContext: PaymentTelemetryContext;
} & ButtonProps;

export type OfferUnavailableErrorMessage =
    | {
          paymentDisabled: true;
          message: string;
          hideBillingCountry: boolean;
      }
    | {
          paymentDisabled: false;
          message: never;
          hideBillingCountry: never;
      }
    | undefined;

export const PayButton = ({
    taxCountry,
    vatNumber,
    disabled,
    children,
    paymentFacade,
    suffix,
    as: Component = Button,
    className: classNameProp,
    paypalClassName,
    formInvalid,
    onClick,
    product,
    telemetryContext,
    ...rest
}: Props) => {
    const { APP_NAME } = useConfig();

    const suffixElement = useMemo(() => {
        if (isFunction(suffix)) {
            return suffix(paymentFacade.selectedMethodType);
        }

        return suffix;
    }, [paymentFacade.selectedMethodType, suffix]);

    const handleOnClick = (payload: PayButtonOnClickPayload) => {
        if (paymentFacade.checkResult) {
            checkoutTelemetry.reportPayment({
                stage: 'attempt',
                userCurrency: paymentFacade.user?.Currency,
                subscription: paymentFacade.subscription,
                selectedCycle: paymentFacade.checkResult.Cycle,
                selectedPlanIDs: paymentFacade.checkResult.requestData.Plans,
                selectedCurrency: paymentFacade.currency,
                selectedCoupon: paymentFacade.checkResult.Coupon?.Code,
                build: APP_NAME,
                product,
                context: telemetryContext,
                amount: paymentFacade.checkResult.AmountDue,
                paymentMethodType: paymentFacade.selectedMethodType,
                paymentMethodValue: paymentFacade.selectedMethodValue,
            });
        }

        onClick?.(payload);
    };

    /**
     * If we can't edit VAT number inline (shouldEditInModal is true) then it means that error states if
     * {@link useVatNumber} are not applicable, and we need to skip them
     */
    const mustCheckVatNumberErrors = vatNumber && !vatNumber?.shouldEditInModal;

    const submitButton = (() => {
        const isChargebeePaypal = paymentFacade.selectedMethodValue === PAYMENT_METHOD_TYPES.CHARGEBEE_PAYPAL;
        const isApplePay = paymentFacade.selectedMethodValue === PAYMENT_METHOD_TYPES.APPLE_PAY;
        const isGooglePay = paymentFacade.selectedMethodValue === PAYMENT_METHOD_TYPES.GOOGLE_PAY;
        const submitButtonDisabled =
            !taxCountry.billingAddressValid || (mustCheckVatNumberErrors && !vatNumber?.vatFormValid) || disabled;

        const className = clsx(submitButtonDisabled && 'cursor-not-allowed', classNameProp);

        if (isChargebeePaypal) {
            const chargebeeWrapperClassName = paypalClassName === undefined ? className : paypalClassName;

            return (
                <ChargebeePaypalButton
                    chargebeePaypal={paymentFacade.chargebeePaypal}
                    iframeHandles={paymentFacade.iframeHandles}
                    disabled={submitButtonDisabled}
                    className={chargebeeWrapperClassName}
                    formInvalid={formInvalid}
                    loading={rest.loading}
                    width="100%"
                    onClick={handleOnClick}
                />
            );
        } else if (isApplePay) {
            return (
                <ApplePayButton
                    applePay={paymentFacade.applePay}
                    iframeHandles={paymentFacade.iframeHandles}
                    disabled={submitButtonDisabled}
                    formInvalid={formInvalid}
                    loading={rest.loading}
                    onClick={handleOnClick}
                />
            );
        } else if (isGooglePay) {
            return (
                <GooglePayButton
                    googlePay={paymentFacade.googlePay}
                    iframeHandles={paymentFacade.iframeHandles}
                    disabled={submitButtonDisabled}
                    formInvalid={formInvalid}
                    loading={rest.loading}
                    onClick={handleOnClick}
                />
            );
        } else {
            return (
                <Component
                    type="submit"
                    disabled={submitButtonDisabled}
                    className={className}
                    onClick={() => {
                        handleOnClick({ type: 'card', source: 'real-button' });
                    }}
                    {...rest}
                >
                    {children}
                </Component>
            );
        }
    })();

    const errorMessage =
        taxCountry?.billingAddressErrorMessage || (mustCheckVatNumberErrors && vatNumber?.vatFormErrorMessage);

    if (!errorMessage) {
        return (
            <>
                {submitButton}
                {suffixElement}
            </>
        );
    }

    return (
        <>
            <Tooltip title={errorMessage} openDelay={0} closeDelay={0}>
                {/* div is needed to enable the tooltip even if the button element is disabled */}
                <div>{submitButton}</div>
            </Tooltip>
            {suffixElement}
        </>
    );
};
